import Vuetable from 'vuetable-2/src/components/Vuetable';
import Avatar from '@/components/Avatar/Avatar';
import LabelList from '@/components/LabelList';
import Modal from '@/components/Modal/Modal';
import Button from '@/components/Button/Button';
import Input from '@/components/Input/Input';
import Highlight from '@/components/Highlight';
import CustomTable from '@/components/Table';
import Pagination from '@/components/Pagination';
import sort from '@/utils/Sort';
import Context from '@/models/Context';
import Group from '@/models/core/Group';
import { mapActions, mapGetters } from 'vuex';
import ToastWrapper from '@/components/ToastWrapper/ToastWrapper';
import MultiSelectSearch from '../../MultiSelectSearch/MultiSelectSearch';

export default {
  name: 'MemberTable',
  components: {
    Vuetable,
    Avatar,
    LabelList,
    Button,
    Input,
    Modal,
    ToastWrapper,
    MultiSelectSearch,
    Highlight,
    Pagination,
    CustomTable
  },
  props: ['members', 'createInvite'],
  watch: {
    members(newValue, oldValue) {
      this.reset = true;
    }
  },
  data() {
    return {
      reset: false,
      currentPage: 1,
      paginationItems: 10,
      sortOrder: 'asc',
      sortColumn: 'user.fullName',
      single: {
        member: null,
        candidateText: '',
        original: null
      },
      selectedUsers: [],
      searchFor: [],
      modal: {
        delete: {
          single: false,
          multiple: false
        },
        update: false,
        create: false
      },
      newInvites: {
        email: [],
        message: ''
      },
      columns: [
        { name: '', type: 'Avatar', key: 'user.avatarUrl' },
        {
          name: 'Name',
          type: 'MultiLine',
          key: 'user.fullName',
          secondary: 'user.username',
          has_sort: true
        },
        { name: 'Roles', type: 'Default', key: 'level', has_sort: true },
        { name: 'Groups', type: 'LabelList', key: 'groups' }
      ],
      actions: [
        { icon: 'edit', method: this.updateSingle },
        { icon: 'delete', method: this.deleteSingle }
      ]
    };
  },
  computed: {
    shouldHighlight() {
      const highlight = [];

      this.searchFor.map(search => {
        if (search.type === 'fuzzy') {
          highlight.push(search.id);
        }
      });

      return highlight;
    },
    disable() {
      return [
        Context.query()
          .where(context => {
            return context.id === 'current';
          })
          .with('user')
          .first().membership.id
      ];
    },
    searchGroups() {
      const groups = Group.query()
        .orderBy('name')
        .get()
        .map(group => {
          return { id: group.id, name: `Group: ${group.name}`, type: 'group' };
        });

      const options = [
        {
          groupName: 'Groups',
          groupValues: groups
        }
      ];

      return options;
    },
    searchOptions() {
      const groups = Group.query()
        .orderBy('name')
        .get()
        .map(group => {
          return { id: group.id, name: `Group: ${group.name}`, type: 'group' };
        });

      const roles = [
        { id: 'creator', name: 'Role: Creator', type: 'role' },
        { id: 'editor', name: 'Role: Editor', type: 'role' },
        { id: 'master', name: 'Role: Master', type: 'role' },
        { id: 'publisher', name: 'Role: Publisher', type: 'role' }
      ];

      const options = [
        {
          groupName: 'Groups',
          groupValues: groups
        },
        {
          groupName: 'Roles',
          groupValues: roles
        }
      ];

      return options;
    },
    filteredMembers() {
      return this.members.filter(current => {
        let shouldReturn = !this.searchFor.length;

        this.searchFor.map(search => {
          if (search.type === 'fuzzy' && !shouldReturn) {
            shouldReturn =
              current.user.fullName.toLowerCase().indexOf(search.id) > -1 ||
              current.user.username.toLowerCase().indexOf(search.id) > -1;
          }

          if (search.type === 'role' && !shouldReturn) {
            shouldReturn = current.level.toLowerCase().indexOf(search.id) > -1;
          }

          if (search.type === 'group' && !shouldReturn) {
            shouldReturn = current.groups_collection.includes(search.id);
          }
        });

        if (shouldReturn) {
          return current;
        }
      });
    },
    deleteCandidateText() {
      return this.selectedUsers.length === 1
        ? this.selectedUsers[0].user.fullName.trim().length
          ? this.selectedUsers[0].user.fullName
          : `${this.selectedUsers.length} user`
        : `${this.selectedUsers.length} users`;
    },
    isDeleting: function() {
      return this._isDeleting();
    },
    isUpdating: function() {
      return this._isUpdating();
    }
  },
  methods: {
    ...mapActions({
      _newToast: 'toasts/_addToast',
      _memberRemove: 'entities/memberships/remove',
      _memberUpdate: 'entities/memberships/update'
    }),
    ...mapGetters({
      _isDeleting: 'entities/memberships/isDeleting',
      _isUpdating: 'entities/memberships/isUpdating'
    }),
    resetPage() {
      this.selectedUsers = [];
      this.reset = true;
    },
    onMultiAction(value) {
      this.selected.push(value);
    },
    select(value) {
      if (this.selectedUsers.includes(value)) {
        this.selectedUsers = this.selectedUsers.filter(current => {
          return current !== value;
        });
      } else {
        this.selectedUsers.push(value);
      }
    },
    updateSingle(value) {
      this.single.candidateText = value.user.fullName.trim().length
        ? value.user.fullName
        : 'this user';
      this.single.member = Object.assign({}, value);
      this.single.member.groups_collection = value.groups.map(group => {
        return { id: group.id, name: group.name };
      });
      this.single.original = value;
      this.modal.update = true;
    },
    updateSingleAction() {
      this.single.member.groups_collection = this.single.member.groups_collection.map(
        group => {
          return group.id;
        }
      );

      this._memberUpdate(this.single.member).then(() => {
        this._newToast({
          text: 'Member has been successfully updated',
          variant: 'good',
          duration: 2000
        });
        this.single.original = null;
        this.single.member = null;
        this.modal.update = false;
      });
    },
    deleteSingle(value) {
      this.single.candidateText = value.user.fullName.trim().length
        ? value.user.fullName
        : 'this user';
      this.single.member = value;
      this.modal.delete.single = true;
    },
    deleteSingleAction() {
      this._memberRemove([this.single.member]).then(() => {
        this.single.candidateText = '';
        this.single.member = null;
        this.modal.delete.single = false;

        this._newToast({
          text: 'Member has been successfully removed',
          variant: 'good',
          duration: 2000
        });
      });
    },
    deleteSelection() {
      this._memberRemove(this.selectedUsers).then(() => {
        this.modal.delete.multiple = false;

        if (this.allSelected) {
          this.updateCurrentPage(1);
        } else {
          this.selectedUsers = [];
        }

        this._newToast({
          text: 'Members have been successfully removed',
          variant: 'good',
          duration: 2000
        });
      });
    }
  }
};
